var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Plugin core and UI components
import { createPluginUI } from 'molstar/lib/mol-plugin-ui';
import { DefaultPluginUISpec } from 'molstar/lib/mol-plugin-ui/spec'; // Use PluginUISpec for custom UI options
import { PluginConfig } from 'molstar/lib/mol-plugin/config';
// Asset loader for external file access
import { Asset } from 'molstar/lib/mol-util/assets';
// Structural data models and selection
import { Structure, StructureSelection } from 'molstar/lib/mol-model/structure';
import { StructureProperties, Unit } from 'molstar/lib/mol-model/structure';
// Secondary structure feature extraction
import { SecondaryStructureProvider } from 'molstar/lib/mol-model-props/computed/secondary-structure';
import { SecondaryStructureType } from 'molstar/lib/mol-model/structure/model/types';
// Color theme systems and theme context
import { ColorTheme } from 'molstar/lib/mol-theme/color';
// Color handling and legends
import { Color } from 'molstar/lib/mol-util/color';
import { TableLegend } from 'molstar/lib/mol-util/legend';
// Scripting system for defining selections
import { Script } from 'molstar/lib/mol-script/script';
import { MolScriptBuilder } from 'molstar/lib/mol-script/language/builder';
// React 18 renderer for plugin UI
import { renderReact18 } from 'molstar/lib/mol-plugin-ui/react18';
// Stylesheets
import 'molstar/build/viewer/molstar.css';
import { BACKEND_URL } from './backendClient';
// ============================================
// Main class to encapsulate Mol*
// ============================================
class MolStarWrapper {
    constructor() {
        // Concurrency control: ensure only latest load renders
        this.currentLoadToken = 0;
        this.lastIssuedToken = 0;
        // ? TODO : reactivate to use further selection functionnalities
        // selectionMode = false;// Track whether selection is active or not
        this.bindingSiteSelectionMode = false; // Track whether binding site selection is active or not
        this.residueSelectionMode = false; // Track whether multiple residue selection is active or not
        // Mapping from chainGroupId to binding site number (1, 2, 3, ...)
        // This ensures consistent numbering across different PDB structures
        this.bindingSiteMapping = new Map();
        // Store selected residues for custom binding site creation
        this.selectedResidues = [];
        // Store reference to main receptor structure (to preserve it when removing centroid markers)
        this.mainStructureRef = null;
        // Concurrency control for centroid display: ensure only latest display renders
        this.currentCentroidToken = 0;
        // Track the key of the currently loaded main structure to avoid redundant full reloads
        this.currentlyLoadedKey = null;
        // Global path configuration based on execution mode
        this.getAdaptedPath = (filePath) => {
            const JOB_EXECUTION_MODE = process.env.JOB_EXECUTION_MODE || 'local';
            return JOB_EXECUTION_MODE === 'ecs'
                ? filePath.replace('/userSessions/', '/mnt/data/userSessions/') // EFS path
                : filePath; // Local path
        };
        /**
         * Add a colored representation to a residue range.
         * Can render surface, atoms, or both (dual-layer).
         *
         * @param rootStructure - The main loaded structure
         * @param start - Start of residue range (auth_seq_id)
         * @param end - End of range (inclusive)
         * @param colorHex - Color to use (hex)
         * @param type - Main representation type
         * @param alpha - Transparency for surface (if applicable)
         * @param dualLayer - If true, adds ball-and-stick atoms inside surface
         */
        this.addColoredRegion = (rootStructure_1, start_1, end_1, colorHex_1, type_1, ...args_1) => __awaiter(this, [rootStructure_1, start_1, end_1, colorHex_1, type_1, ...args_1], void 0, function* (rootStructure, start, end, colorHex, type, alpha = 0.5, dualLayer = false) {
            // Define the range selector script
            const expression = MolScriptBuilder.struct.generator.atomGroups({
                'residue-test': MolScriptBuilder.core.rel.inRange([
                    MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(), start, end // Define range by auth_seq_id = sequence ID
                ])
            });
            // Wrap as a selection query
            const query = {
                label: `Residues ${start}-${end}`,
                description: 'Custom residue range',
                category: 'Custom',
                isHidden: false,
                priority: 100,
                referencesCurrent: false,
                expression,
                ensureCustomProperties: () => __awaiter(this, void 0, void 0, function* () { }),
                getSelection: (plugin, runtime, structure) => __awaiter(this, void 0, void 0, function* () {
                    return Script.getStructureSelection(expression, structure);
                }),
                query: undefined
            };
            // Attempt to build a visual component for the selected range
            const component = yield this.plugin.builders.structure.tryCreateComponentFromSelection(rootStructure.cell, query, `region-${start}-${end}`);
            if (!component) {
                console.warn(`Could not create component for region ${start}-${end}`);
                return;
            }
            // Add the selected representation (surface or atoms)
            yield this.plugin.builders.structure.representation.addRepresentation(component, {
                type,
                typeParams: type === 'spacefill' || type === 'ball-and-stick'
                    ? { sizeFactor: 0.6 } // Atom scale for spacefill/ball-stick
                    : { alpha }, // Surface transparency for gaussian surface
                color: 'uniform',
                colorParams: { value: Color(colorHex) }
            });
            // Optionally, add a ball-and-stick layer inside
            if (dualLayer) {
                yield this.plugin.builders.structure.representation.addRepresentation(component, {
                    type: 'ball-and-stick',
                    typeParams: { sizeFactor: 0.6 },
                    color: 'element-symbol' // Default atom-based coloring
                });
            }
        });
        /**
         * TEMPORARY TEST FUNCTION - To be removed after debugging
         * Captures the event object when clicking on a residue
         */
        this.captureEventObject = (event) => {
            console.log('🔍 Event object:', event);
            return event;
        };
        /**
         * Analyzes the current structure to identify binding sites
         * This is a test function that can be called from the console
         */
    }
    // ================================
    // Load Structure
    // ================================
    /**
     * Downloads and parses a structure file from the RCSB based on a given PDB ID.
     * @param pdbId - The 4-character PDB identifier
     * @returns trajectory - Parsed trajectory object from the PDB data
     */
    loadStructureFromUrl(pdbId) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = `https://files.rcsb.org/download/${pdbId}.pdb`; // Construct PDB download URL
            const data = yield this.plugin.builders.data.download({ url: Asset.Url(url) }); // Download the structure
            const trajectory = yield this.plugin.builders.structure.parseTrajectory(data, 'pdb'); // Parse the structure into a trajectory
            return trajectory;
        });
    }
    /**
     * Loads a PDB structure file by fetching it from the backend Express server.
     * ? @param pdbId - The file path of the PDB structure to load from back-end (e.g., "4DLQ")
     * @returns A Promise resolving to the parsed trajectory, or null if loading failed
     */
    loadStructureFromStorage(pdbId, pdbFilePath) {
        return __awaiter(this, void 0, void 0, function* () {
            // ? Construct the URL for the PDB file served statically by the Express backend
            const baseUrl = BACKEND_URL; // Base URL of the backend server
            const adaptedPath = this.getAdaptedPath(pdbFilePath);
            const localUrl = `${baseUrl}${adaptedPath}`;
            try {
                // Get authentication token from localStorage
                const authToken = localStorage.getItem('accessToken');
                // Fetch the file via HTTP from the backend server with authentication
                const response = yield fetch(localUrl, {
                    headers: authToken ? {
                        'Authorization': `Bearer ${authToken}`
                    } : {}
                });
                // Handle file-not-found (404) response
                if (!response.ok) {
                    console.error(`No PDB file found (status ${response.status}): ${localUrl}`);
                    return null;
                }
                // Read the file content as plain text (PDB format)
                const pdbText = yield response.text();
                // Create a Mol* data object from the raw text
                const data = yield this.plugin.builders.data.rawData({
                    data: pdbText,
                    label: pdbId
                });
                // Parse the raw PDB data into a Mol* trajectory
                const trajectory = yield this.plugin.builders.structure.parseTrajectory(data, 'pdb');
                // Return the parsed trajectory
                return trajectory;
            }
            catch (error) {
                // Log any unexpected errors (network failures, parsing errors, etc.)
                console.error(`Error while loading PDB "${pdbId}" at the path "${pdbFilePath}:`, error);
                alert(`An error occurred while loading the PDB file "${pdbId}". Check the console for details.`);
                return null;
            }
        });
    }
    /**
     * Applies a visual preset, and optionally configures selection for the structure loaded.
     * @param pdbId - Optional PDB ID entered by the user (default if none)
     */
    /** Returns the key of the currently loaded main structure (or null if none). */
    getLoadedKey() {
        return this.currentlyLoadedKey;
    }
    loadStructureWithStyle(pdbId, pdbFilePath) {
        return __awaiter(this, void 0, void 0, function* () {
            const finalId = pdbId === null || pdbId === void 0 ? void 0 : pdbId.trim().toLowerCase(); // Normalize PDB ID
            // Issue a new token for this load request
            const token = ++this.lastIssuedToken;
            this.currentLoadToken = token;
            // Clear binding site mapping and loaded-key when starting a new load
            this.bindingSiteMapping.clear();
            this.currentlyLoadedKey = null;
            yield this.plugin.clear(); // Clear any existing scene content
            // If a newer load started while clearing, abort
            if (token !== this.currentLoadToken) {
                console.warn('[Mol*] Aborting peptide load: superseded by a newer request');
                return;
            }
            const trajectory = yield this.loadStructureFromStorage(finalId, pdbFilePath); // ! Test : replace with loadStructureFromUrl 
            // Do nothing if no structure was successfully loaded
            if (!trajectory) {
                console.error(`Failed to parse PDB data for "${pdbId}"`);
                this.updatePanelFromStructure(`❌ Structure "${pdbId}" could not be loaded.`);
                return;
            }
            // If a newer load started while fetching/parsing, abort render
            if (token !== this.currentLoadToken) {
                console.warn('[Mol*] Aborting peptide render: superseded by a newer request');
                return;
            }
            this.updatePanelFromStructure(`✅ Structure <b>${pdbId}</b> loaded.<br>Load another structure to start working with it.`);
            // Apply one of several visual styles (comment/uncomment as needed)
            // await this.applyVillinStyle(trajectory);             // ✅ Apply Villin visual theme
            // await this.applyGainStyle(trajectory);                // 🔁 GAIN visual theme 
            // Peptide IDs are prefixed with "peptide-" — use detailed atom rendering for them.
            const isPeptide = !!(finalId === null || finalId === void 0 ? void 0 : finalId.startsWith('peptide-'));
            yield this.applyBasicModulationStyle(trajectory, { addAtomicDetail: isPeptide }); // 🔁 Alternative: basic rendering
            // Record the key of the loaded structure for deduplication by callers
            this.currentlyLoadedKey = finalId !== null && finalId !== void 0 ? finalId : null;
            // Store reference to main structure (receptor) - first structure in hierarchy
            setTimeout(() => {
                const structures = this.plugin.managers.structure.hierarchy.current.structures;
                if (structures.length > 0) {
                    this.mainStructureRef = structures[0].cell;
                }
                this.analyzeBindingSites();
            }, 100);
            // Optionally preselect a residue by name
            yield this.configureAfterLoad({
            // preselectResidueName: 'HEM', // Example: 'HEM' for 1tqn
            // ? showAllInteractions: true, // Optional: display all non-covalent contacts
            });
            // ! Bind structure-specific presets to the dropdown
            // this.setupPresetSwitcher(trajectory);
        });
    }
    /**
     * Adds a PDB structure to the current scene without clearing existing content.
     * Use this when displaying receptor + custom binding site together (so the second load does not wipe the first).
     */
    addStructureWithStyle(pdbId, pdbFilePath) {
        return __awaiter(this, void 0, void 0, function* () {
            const finalId = pdbId === null || pdbId === void 0 ? void 0 : pdbId.trim().toLowerCase();
            const trajectory = yield this.loadStructureFromStorage(finalId, pdbFilePath);
            if (!trajectory) {
                console.error(`[Mol*] Failed to add structure "${pdbId}"`);
                return;
            }
            yield this.applyBasicModulationStyle(trajectory, { addAtomicDetail: true });
        });
    }
    /**
     * Loads multiple docking poses (PDB files) as separate models inside a single merged PDB stream.
     * Mol* will expose a model selector to switch between poses.
     */
    loadDockingPosesAsModels(pdbId, posePaths) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                console.log(`[Mol*] Starting docking load for ${pdbId} with ${posePaths.length} poses`);
                // Issue a new token for this load request
                const token = ++this.lastIssuedToken;
                this.currentLoadToken = token;
                yield this.plugin.clear();
                // If a newer load started while clearing, abort
                if (token !== this.currentLoadToken) {
                    console.warn('[Mol*] Aborting docking load: superseded by a newer request');
                    return;
                }
                // Get authentication token from localStorage
                const authToken = localStorage.getItem('accessToken');
                // Adapt paths based on execution mode
                const adaptedPaths = posePaths.map(path => this.getAdaptedPath(path));
                // OPTIMIZATION 1: Check file existence first (HEAD requests are faster)
                console.log('[Mol*] Checking pose file availability...');
                const existingPoses = [];
                const checkPromises = adaptedPaths.map((path, index) => __awaiter(this, void 0, void 0, function* () {
                    const url = `${BACKEND_URL}${path}`;
                    try {
                        const res = yield fetch(url, {
                            method: 'HEAD',
                            headers: authToken ? {
                                'Authorization': `Bearer ${authToken}`
                            } : {}
                        });
                        if (res.ok) {
                            existingPoses.push(path);
                            return { path, index, exists: true };
                        }
                    }
                    catch (e) {
                        console.warn(`[Mol*] Pose ${index + 1} not available:`, path);
                    }
                    return { path, index, exists: false };
                }));
                const checkResults = yield Promise.all(checkPromises);
                const availablePoses = checkResults.filter(r => r.exists);
                if (availablePoses.length === 0) {
                    console.warn('[Mol*] No docking poses available');
                    return;
                }
                // If a newer load started while checking, abort
                if (token !== this.currentLoadToken) {
                    console.warn('[Mol*] Aborting docking load: superseded by a newer request');
                    return;
                }
                // OPTIMIZATION 2: Load only existing poses in parallel
                console.log('[Mol*] Loading pose data...');
                const loadPromises = availablePoses.map((_a) => __awaiter(this, [_a], void 0, function* ({ path, index }) {
                    const url = `${BACKEND_URL}${path}`;
                    try {
                        const res = yield fetch(url, {
                            headers: authToken ? {
                                'Authorization': `Bearer ${authToken}`
                            } : {}
                        });
                        if (res.ok) {
                            const pdb = yield res.text();
                            return { pdb, index: index + 1 };
                        }
                    }
                    catch (e) {
                        console.warn(`[Mol*] Failed to load pose ${index + 1}:`, e);
                    }
                    return null;
                }));
                const poseData = (yield Promise.all(loadPromises)).filter(p => p !== null);
                if (poseData.length === 0) {
                    console.warn('[Mol*] No valid pose data loaded');
                    return;
                }
                // If a newer load started while loading, abort
                if (token !== this.currentLoadToken) {
                    console.warn('[Mol*] Aborting docking render: superseded by a newer request');
                    return;
                }
                // OPTIMIZATION 3: Build multi-model structure
                console.log(`[Mol*] Building multi-model structure with ${poseData.length} poses...`);
                const blocks = poseData.map(({ pdb, index }) => `MODEL ${index}\n${pdb}\nENDMDL`);
                const merged = blocks.join('\n');
                const data = yield this.plugin.builders.data.rawData({ data: merged, label: pdbId });
                const trajectory = yield this.plugin.builders.structure.parseTrajectory(data, 'pdb');
                console.log('[Mol*] Applying docking visualization style...');
                yield this.applyBasicModulationStyle(trajectory, { addAtomicDetail: true });
                // Analyze structure to create binding site mapping
                this.analyzeBindingSites();
                console.log(`[Mol*] Docking load completed successfully with ${poseData.length} poses`);
            }
            catch (e) {
                console.error('Failed to load docking poses as models:', e);
            }
        });
    }
    // ================================
    // UI and Interaction
    // ================================
    /**
     * Update the structure info panel with structure loading info or a message.
     * @param selected - Array of selected residues (with sequence IDs)
     * @param customMessage - Optional message to override selection list
     */
    updatePanelFromStructure(customMessage) {
        const infoPanel = document.getElementById('structure-info'); // Locate info panel
        if (!infoPanel)
            return; // Abort if not found
        // Show loading error or default text
        infoPanel.innerHTML = customMessage !== null && customMessage !== void 0 ? customMessage : 'Load a structure to start working with it.';
    }
    /**
     * Updates the residue info panel with selection info or a message.
     * @param selected - Array of selected residues (with sequence IDs)
     * @param customMessage - Optional message to override selection list
     */
    // ? TODO : reactivate to use further selection functionnalities
    // private updatePanelFromSelection(selected: any[], customMessage?: string) {
    //     const infoPanel = document.getElementById('sel-info'); // Locate info panel
    //     if (!infoPanel) return; // Abort if not found
    //     // Show custom message or selected positions
    //     infoPanel.textContent = customMessage
    //         ?? (selected.length > 0
    //             ? selected.map(item => item.displayName).join(', ')
    //             : 'Click a residue or enter a residue name to get information.');
    // }
    /**
     * Binds all HTML UI elements to their respective event handlers.
     * This method is called once during initialization after the plugin is ready.
     *
     * Separating this logic from `init()` improves readability and modularity.
     */
    bindUIEvents() {
        // ? TODO : reactivate to use further selection functionnalities
        // ==============================
        // Toggle selection mode on/off
        // ==============================
        // Button that activates or deactivates interactive selection mode
        // const toggleBtn = document.getElementById('toggle-sel-residue')!;
        // Label that displays current selection mode state
        // const toggleLabel = document.getElementById('toggle-sel-residue-label')!;
        // toggleBtn.addEventListener('click', () => {
        //     this.selectionMode = !this.selectionMode; // Flip internal state
        //     this.plugin.selectionMode = this.selectionMode; // Sync with plugin
        //     // Update label based on mode
        //     toggleLabel.textContent = this.selectionMode
        //         ? 'Click a residue to select it.'
        //         : 'Selection disabled.';
        //     // If residue selection is enabled, disable binding site selection
        //     if (this.selectionMode) {
        //         this.bindingSiteSelectionMode = false;
        //         bindingSiteToggleLabel.textContent = 'Binding site click selection disabled';
        //         (document.getElementById('toggle-sel-bindingsite') as HTMLInputElement).checked = false;
        //     }
        //     if (!this.selectionMode) {
        //         this.clearCurrentSelection(); // Clear all selections visually and in the info panel
        //     }
        // });
        // ==============================
        // Toggle binding site selection mode on/off
        // ==============================
        // Note: Binding site toggle is handled by bindBindingSiteEvents() 
        // which is called when the binding site interface is dynamically created
        // ? TODO : reactivate to use further selection functionnalities
        // ==============================
        // Clear selection button
        // ==============================
        // Button that clears all currently selected residues
        // const clearBtn = document.getElementById('clear-selection-btn')!;
        // clearBtn.addEventListener('click', () => {
        //     this.clearCurrentSelection(); // Clear all selections and reset UI
        // });
        // ==============================
        // Select residue by name button
        // ==============================
        // Button that triggers residue lookup by name (e.g., 'HEM')
        // const selectResidueBtn = document.getElementById('select-residue-btn')!;
        // selectResidueBtn.addEventListener('click', () => {
        //     // Get the residue name from input field and trim it
        //     const residueName = (document.getElementById('residue-name-input') as HTMLInputElement).value.trim();
        //     if (residueName) {
        //         // Attempt selection by name
        //         this.selectResidueByName(residueName);
        //     }
        // });
    }
    /**
     * Binds binding site UI elements to their event handlers.
     * This method is called when the binding site interface is dynamically created.
     */
    bindBindingSiteEvents() {
        var _a;
        console.log('🔗 Binding binding site events...');
        // Button that activates or deactivates binding site selection mode
        const bindingSiteToggleBtn = document.getElementById('toggle-sel-bindingsite');
        // Label that displays current binding site selection mode state
        const bindingSiteToggleLabel = document.getElementById('toggle-sel-bindingsite-label');
        console.log('🔍 Binding site elements found:', {
            bindingSiteToggleBtn: !!bindingSiteToggleBtn,
            bindingSiteToggleLabel: !!bindingSiteToggleLabel
        });
        if (bindingSiteToggleBtn && bindingSiteToggleLabel) {
            // Remove any existing event listeners to prevent duplicates
            const newToggleBtn = bindingSiteToggleBtn.cloneNode(true);
            (_a = bindingSiteToggleBtn.parentNode) === null || _a === void 0 ? void 0 : _a.replaceChild(newToggleBtn, bindingSiteToggleBtn);
            newToggleBtn.addEventListener('click', () => {
                console.log('🔘 Binding site toggle clicked - current mode:', this.bindingSiteSelectionMode);
                this.bindingSiteSelectionMode = !this.bindingSiteSelectionMode; // Flip internal state
                // Update label based on mode
                bindingSiteToggleLabel.textContent = this.bindingSiteSelectionMode
                    ? 'Click a binding site to select it.'
                    : 'Binding site click selection disabled';
                // If binding site selection is enabled, enable Mol* selection
                if (this.bindingSiteSelectionMode) {
                    // Disable residue selection mode
                    this.residueSelectionMode = false;
                    const residueToggle = document.getElementById('toggle-sel-residues');
                    if (residueToggle) {
                        residueToggle.checked = false;
                    }
                    this.clearCurrentSelection();
                    // Enable Mol* selection mode for binding sites
                    this.plugin.selectionMode = true;
                    console.log('✅ Binding site selection mode enabled - Mol* selection active');
                }
                else {
                    // Disable Mol* selection when binding site mode is off
                    this.plugin.selectionMode = false;
                    console.log('❌ Binding site selection mode disabled - Mol* selection inactive');
                }
                // Always allow manual input regardless of toggle state
                const input = document.getElementById('binding-site-input');
                if (input) {
                    input.disabled = false;
                    input.style.opacity = '1';
                }
                // Validate submit button
                if (typeof window.validateBindingSiteSubmit === 'function') {
                    window.validateBindingSiteSubmit();
                }
            });
            console.log('✅ Binding site toggle event listener added');
        }
        else {
            console.log('⚠️ Binding site toggle elements not found:', { bindingSiteToggleBtn, bindingSiteToggleLabel });
        }
    }
    /**
     * Clears all currently selected residues from the viewer and UI.
     * This resets selection visuals, plugin state, and the info panel.
     */
    clearCurrentSelection() {
        // Deselect all regions visually in 3D viewer
        this.plugin.managers.interactivity.lociSelects.deselectAll();
        // Clear plugin's internal selection state
        this.plugin.managers.structure.selection.clear();
        // ? TODO : reactivate to use further selection functionnalities
        // Clear the residue name input field
        // const residueNameInput = document.getElementById('residue-name-input') as HTMLInputElement;
        // if (residueNameInput) {
        //     residueNameInput.value = '';
        // }
        // ? TODO : reactivate to use further selection functionnalities
        // Reset the info panel text content
        // this.updatePanelFromSelection([]);
    }
    /**
     * Selects all residues in the structure matching a given residue name.
     * @param resName - Residue name to search for (e.g. 'HEM')
     */
    // ? TODO : reactivate to use further selection functionnalities
    // private async selectResidueByName(resName: string) {
    //     const structure = this.plugin.managers.structure.hierarchy.current.structures[0]?.cell.obj?.data; 
    //     // ? 'cell.obj.data' = the structure data of the first loaded structure
    //     if (!structure) return;
    //     // Build a selection script that filters by label_comp_id (residue name)
    //     const selection = Script.getStructureSelection(Q =>
    //         Q.struct.generator.atomGroups({
    //             'residue-test': Q.core.rel.eq([
    //                 Q.struct.atomProperty.macromolecular.label_comp_id(), resName // Match input name
    //             ]),
    //             'group-by': Q.struct.atomProperty.macromolecular.residueKey() // Group by unique residue
    //         }),
    //         structure
    //     );
    //     const loci = StructureSelection.toLociWithSourceUnits(selection); // Convert selection to loci
    //     if (loci.elements.length === 0) {
    //         // If no match found, update with error message
    //         this.updatePanelFromSelection([], `No residue with name "${resName}" found.`);
    //         return;
    //     }
    //     // Select and focus camera on the residue(s) and display non-covalent interactions around it
    //     this.applyFocusWithInteractions(loci);
    //     // TODO: Fix residue selection by name to correctly extract and display residue sequence numbers
    //     // For now, just show that residues were found without specific numbers
    //     // Update the UI panel with simple message
    //     this.updatePanelFromSelection(
    //         [],
    //         `Residues found with name '${resName}'`
    //     );
    // }
    // ================================
    // Initial structure display config
    // ================================
    /**
     * Post-loading configuration logic for displaying the structure.
     * Can automatically select a residue and/or show all interactions once a structure is loaded.
     *
     * @param options - Configuration object:
     * @param options.preselectResidueName - Optional residue name (e.g., 'HEM') to auto-select
     * @param options.showAllInteractions - If true, show all contacts after loading
     */
    configureAfterLoad(options) {
        return __awaiter(this, void 0, void 0, function* () {
            // ? TODO : reactivate to use further selection functionnalities
            // If a residue name was provided, try selecting it after structure load
            // if (options.preselectResidueName) {
            //     await this.selectResidueByName(options.preselectResidueName);
            // }
            // If requested, display non-covalent interactions for the entire structure
            if (options.showAllInteractions) {
                yield this.showInteractionsForWholeStructure();
            }
        });
    }
    /**
     * Attach the preset dropdown to the current trajectory.
     * Allows users to switch between representations like unitcell, supercell, crystal contacts, etc.
     *
     * @param trajectory - The structure trajectory currently loaded in the viewer
     */
    // private setupPresetSwitcher(trajectory: any) { // TODO: Improve the function logic if improvements are needed
    //     const presetSelect = document.getElementById('preset-select') as HTMLSelectElement;
    //     const presetDesc = document.getElementById('preset-desc') as HTMLSpanElement;
    //     // Reset the description field when initializing
    //     // !presetDesc.textContent = '';
    //     // Description text mapped to each known preset type
    //     const descriptions: Record<string, string> = {
    //         default: "Default view with cartoon and ligand styles.",
    //         unitcell: "Shows the crystallographic unit cell box.",
    //         supercell: "Repeats the unit cell in 3D (e.g. 3×3×3).",
    //         crystalContacts: "Displays symmetry mates and contact molecules.",
    //         'all-models': "Loads all models from the trajectory if multiple."
    //     };
    //     // Replace the dropdown element with a clone to clear old event listeners
    //     const clone = presetSelect.cloneNode(true);
    //     presetSelect.replaceWith(clone);
    //     // Re-bind the new reference after replacement
    //     const newSelect = document.getElementById('preset-select') as HTMLSelectElement;
    //     // On preset change, apply the selected display preset and update the description
    //     newSelect.addEventListener('change', async () => {
    //         const selected = newSelect.value;
    //         // Update the text label under the dropdown
    //         // ! presetDesc.textContent = descriptions[selected] ?? '';
    //         // Apply the corresponding Mol* preset
    //         if (['default', 'unitcell', 'supercell', 'crystalContacts', 'all-models'].includes(selected)) {
    //             await this.plugin.builders.structure.hierarchy.applyPreset(
    //                 trajectory,
    //                 selected as 'default' | 'unitcell' | 'supercell' | 'crystalContacts' | 'all-models'
    //             );
    //         } else {
    //             // Handle unexpected value gracefully
    //             console.error(`Invalid preset selected: ${selected}`);
    //         }
    //     });
    // }
    /**
     * Selects and focuses the entire structure to display global interactions (non-covalent interactions).
     */
    showInteractionsForWholeStructure() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // Access the root structure reference
            const structureRef = (_a = this.plugin.managers.structure.hierarchy.current.structures[0]) === null || _a === void 0 ? void 0 : _a.cell;
            if (!structureRef)
                return;
            // Get the actual structure data from the cell
            const structure = (_b = structureRef.obj) === null || _b === void 0 ? void 0 : _b.data;
            if (!structure)
                return;
            // Create a full-structure selection
            const loci = Structure.Loci(structure);
            // Apply focus effect (used to trigger contact display as well)
            yield this.plugin.managers.structure.focus.setFromLoci(loci);
        });
    }
    /**
 * Highlights and focuses interactions for multiple selected residues (non-covalent).
 * Accepts an array of residues previously selected and combines them into a single selection.
 *
 * @param selectedLoci - Array of residue objects containing { position } (label_seq_id)
 */
    applyFocusWithInteractions(selectedLoci) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // Get the current structure from the Mol* plugin state
            const structure = (_b = (_a = this.plugin.managers.structure.hierarchy.current.structures[0]) === null || _a === void 0 ? void 0 : _a.cell.obj) === null || _b === void 0 ? void 0 : _b.data;
            if (!structure || !selectedLoci || (Array.isArray(selectedLoci) && selectedLoci.length === 0))
                return;
            let loci;
            // Handle if selectedLoci is already a StructureElement.Loci
            if (!Array.isArray(selectedLoci) && selectedLoci && selectedLoci.structure) {
                loci = selectedLoci;
            }
            else {
                // Extract all label_seq_id positions
                const positions = selectedLoci.map(sel => sel.position);
                console.log("Residue positions to highlight:", positions);
                // Import the script builder locally to avoid 'Q is undefined'
                const Q = MolScriptBuilder;
                let selection;
                if (positions.length === 1) {
                    // If only one residue: use a direct equality condition
                    selection = Script.getStructureSelection(Q => Q.struct.generator.atomGroups({
                        'residue-test': Q.core.rel.eq([
                            Q.struct.atomProperty.macromolecular.label_seq_id(),
                            positions[0]
                        ]),
                        'group-by': Q.struct.atomProperty.macromolecular.residueKey()
                    }), structure);
                }
                else {
                    // If multiple residues: build an OR condition from several eq() clauses
                    const eqConditions = positions.map(pos => Q.core.rel.eq([
                        Q.struct.atomProperty.macromolecular.label_seq_id(),
                        pos
                    ]));
                    // Combine them into one logic.or() clause
                    selection = Script.getStructureSelection(Q => Q.struct.generator.atomGroups({
                        'residue-test': Q.core.logic.or([...eqConditions]),
                        'group-by': Q.struct.atomProperty.macromolecular.residueKey()
                    }), structure);
                }
                // Convert the selection object into a Loci (to apply selection & focus)
                loci = StructureSelection.toLociWithSourceUnits(selection);
            }
            // Apply camera focus and interaction highlight for all selected residues
            yield this.plugin.managers.structure.focus.setFromLoci(loci);
            // Smoothly focus the camera on the selected loci
            this.plugin.managers.camera.focusLoci(loci);
        });
    }
    // ================================
    // Style config
    // ================================
    /**
     * Apply a basic test visualization to the loaded structure.
     * This representation is useful for debugging or lightweight rendering.
     *
     * @param trajectory - The loaded molecular trajectory
     *
     * @property representationPreset - Specifies the type of representation preset to apply. Possible options:
     * - `'protein-and-nucleic'`: Highlights proteins and nucleic acids.
     * - `'polymer-cartoon'`: Displays polymers in a cartoon style.
     * - `'gaussian-surface'`: Renders a smooth surface representation.
     * - `'ball-and-stick'`: Shows atoms and bonds in a ball-and-stick model.
     * - `'spacefill'`: Displays atoms as space-filling spheres.
     *
     * @property representationPresetParams - Additional parameters for the representation preset:
     * - `theme.globalName`: Specifies the color theme. Possible options:
     *   - `'uniform'`: Applies a uniform color to the structure.
     *   - `'secondary-structure'`: Colors based on secondary structure elements.
     *   - `'element-symbol'`: Colors based on element symbols.
     * - `ignoreHydrogens`: A boolean indicating whether to hide hydrogen atoms. Set to `true` to hide them, or `false` to show them.
     * - `quality`: Specifies the rendering quality. Possible options:
     *   - `'auto'`: Automatically selects the best quality based on performance.
     *   - `'low'`: Low rendering quality.
     *   - `'medium'`: Medium rendering quality.
     *   - `'high'`: High rendering quality.
     *   - `'custom'`: Custom quality settings.
     */
    applyBasicModulationStyle(trajectory, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (options === null || options === void 0 ? void 0 : options.addAtomicDetail) {
                // Peptide / docking: use Mol* default preset (no custom theme)
                // so all atom detail, ball-and-stick, hydrogen bonds etc. are preserved.
                yield this.plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default');
            }
            else {
                // Receptor: custom green theme + STP binding site overlay.
                try {
                    this.registerCustomUniformGreenTheme();
                }
                catch (e) {
                    console.warn('[Mol*] Custom uniform theme already registered, skipping re-registration.');
                }
                yield this.plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default', {
                    representationPreset: 'polymer-cartoon',
                    representationPresetParams: {
                        theme: { globalName: 'custom-uniform-green' }
                    }
                });
                // Explicitly add STP (binding site) residues using element-symbol coloring
                try {
                    const structures = this.plugin.managers.structure.hierarchy.current.structures;
                    const mainStructure = structures[structures.length - 1];
                    if (mainStructure === null || mainStructure === void 0 ? void 0 : mainStructure.cell) {
                        const stpExpression = MolScriptBuilder.struct.generator.atomGroups({
                            'residue-test': MolScriptBuilder.core.rel.eq([
                                MolScriptBuilder.struct.atomProperty.macromolecular.label_comp_id(),
                                'STP'
                            ])
                        });
                        const stpQuery = {
                            label: 'Binding Sites (STP)',
                            description: 'STP residues marking binding site positions',
                            category: 'Custom',
                            isHidden: false,
                            priority: 100,
                            referencesCurrent: false,
                            expression: stpExpression,
                            ensureCustomProperties: () => __awaiter(this, void 0, void 0, function* () { }),
                            getSelection: (_plugin, _runtime, structure) => __awaiter(this, void 0, void 0, function* () {
                                return Script.getStructureSelection(stpExpression, structure);
                            }),
                            query: undefined
                        };
                        const component = yield this.plugin.builders.structure.tryCreateComponentFromSelection(mainStructure.cell, stpQuery, 'binding-sites-stp');
                        if (component) {
                            yield this.plugin.builders.structure.representation.addRepresentation(component, {
                                type: 'ball-and-stick',
                                typeParams: { sizeFactor: 0.15 },
                                color: 'element-symbol'
                            });
                        }
                    }
                }
                catch (e) {
                    console.warn('[Mol*] Could not add STP binding site representation:', e);
                }
            }
        });
    }
    /**
     * Enables outline rendering and soft lighting for visual clarity.
     * Useful for illustration-focused styles like Villin or GAIN.
     */
    applyIllustrativeRendering() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            yield ((_a = this.plugin.canvas3d) === null || _a === void 0 ? void 0 : _a.setProps({
                postprocessing: {
                    outline: {
                        name: 'on',
                        params: {
                            scale: 1, // Outline thickness
                            threshold: 0.33, // Edge detection sensitivity
                            color: Color(0x000000), // Black outline
                            includeTransparent: false // Include partially transparent surfaces
                        }
                    },
                    occlusion: { name: 'off', params: {} }, // Disable ambient occlusion
                    shadow: { name: 'off', params: {} } // Disable shadows
                },
                renderer: {
                    colorMarker: false, // No overlay color markers
                    interiorDarkening: 0.2, // Light shading inside structures
                    highlightStrength: 1, // Full highlight strength
                    selectStrength: 1, // Full selection intensity
                    ambientIntensity: 0.5, // Scene light level
                    light: [{
                            intensity: 0.6, // Directional light brightness
                            color: Color(0xeeeeee), // Light grey tone
                            azimuth: 180, // Direction from which light comes
                            inclination: 180 // Vertical angle (180 = flat overhead)
                        }]
                }
            }));
        });
    }
    /**
     * Register a custom color theme for secondary structure types.
     * This assigns specific colors for helices, beta-sheets, and coils.
     */
    registerCustomSecondaryStructureTheme() {
        // Define colors for different secondary structure types
        const helixColor = Color(0xB388EB); // Violet color for helices
        const sheetColor = Color(0x4233FF); // Blue-violet color for beta sheets
        const coilColor = Color(0xCED8DD); // Pale gray for unstructured regions
        /**
         * Factory function that assigns color based on secondary structure at given residue.
         */
        function CustomSecondaryStructureColorTheme(ctx) {
            // Retrieve the computed secondary structure for the structure
            const computed = ctx.structure && SecondaryStructureProvider.get(ctx.structure);
            // Function to assign color to each location
            const color = (location) => {
                if (!(computed === null || computed === void 0 ? void 0 : computed.value) || !Unit.isAtomic(location.unit))
                    return coilColor; // Return coil color if no secondary structure is computed
                const ss = computed.value.get(location.unit.invariantId); // Get secondary structure for the unit
                if (!ss)
                    return coilColor; // Return coil color if no secondary structure is found
                const index = ss.getIndex(location.unit.residueIndex[location.element]); // Get the index of the residue
                const type = ss.type[index]; // Get the type of secondary structure
                if (SecondaryStructureType.is(type, 2 /* SecondaryStructureType.Flag.Helix */))
                    return helixColor; // Return helix color for helices
                if (SecondaryStructureType.is(type, 4 /* SecondaryStructureType.Flag.Beta */))
                    return sheetColor; // Return sheet color for beta sheets
                return coilColor; // Default to coil color for other cases
            };
            return {
                factory: CustomSecondaryStructureColorTheme, // Factory function for the theme
                granularity: 'group', // Granularity level for the theme
                color, // Color function
                props: {}, // No additional properties
                description: 'Custom colors for helices, sheets, and coils', // Description of the theme
                legend: TableLegend([
                    ['Helix', helixColor], // parameter to define helix color
                    ['Sheet', sheetColor],
                    ['Coil', coilColor]
                ])
            };
        }
        // Define and register the provider
        const CustomThemeProvider = {
            name: 'custom-secondary-structure', // Name of the theme
            label: 'Custom Secondary Structure', // Label for the theme
            category: ColorTheme.Category.Misc, // Category of the theme
            factory: CustomSecondaryStructureColorTheme, // Factory function for the theme
            getParams: () => ({}), // No additional parameters
            defaultValues: {}, // Default values for the theme
            isApplicable: (ctx) => !!ctx.structure // Check if the theme is applicable
        };
        this.plugin.representation.structure.themes.colorThemeRegistry.add(CustomThemeProvider); // Register the custom theme provider
    }
    /**
     * Register a uniform theme that always colors the structure in Meddenovo green.
     * This avoids relying on `colorParams` being respected by the built-in `uniform` theme.
     */
    registerCustomUniformGreenTheme() {
        const proteinColor = Color(0x62C5B2); // Meddenovo green for receptor
        function CustomUniformGreenColorTheme(_ctx) {
            const color = (_location) => proteinColor;
            return {
                factory: CustomUniformGreenColorTheme,
                granularity: 'group',
                color,
                props: {},
                description: 'Meddenovo green receptor',
                legend: TableLegend([['Receptor', proteinColor]]),
            };
        }
        const CustomThemeProvider = {
            name: 'custom-uniform-green',
            label: 'Custom Uniform Green',
            category: ColorTheme.Category.Misc,
            factory: CustomUniformGreenColorTheme,
            getParams: () => ({}),
            defaultValues: {},
            isApplicable: (ctx) => !!ctx.structure,
        };
        this.plugin.representation.structure.themes.colorThemeRegistry.add(CustomThemeProvider);
    }
    /**
     * Applies the Villin-style representation:
     * - Cartoon with secondary structure color theme
     * - Transparent surface
     * - Illustrative rendering
     *
     * @param trajectory - The parsed structure trajectory
     */
    applyVillinStyle(trajectory) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Register the custom color theme (if not already registered)
                this.registerCustomSecondaryStructureTheme();
            }
            catch (e) {
                console.warn('[Mol*] Custom theme already registered, skipping re-registration.');
            }
            // Apply cartoon with our custom theme
            yield this.plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default', {
                representationPreset: 'polymer-cartoon',
                representationPresetParams: {
                    theme: { globalName: 'custom-secondary-structure' }
                }
            });
            // Access the first polymer structure component
            const polymer = this.plugin.managers.structure.hierarchy.current.structures[0].components[0];
            // Add a translucent surface layer on top
            yield this.plugin.builders.structure.representation.addRepresentation(polymer.cell, {
                type: 'gaussian-surface',
                typeParams: { alpha: 0.4 },
                color: 'uniform',
                colorParams: { value: Color(0xD8ECF1) } // Soft cyan
            });
            // Apply outline and lighting effects
            yield this.applyIllustrativeRendering();
        });
    }
    /**
     * Apply the GAIN visual preset:
     * - Cartoon backbone
     * - White transparent surface
     * - Highlighted regions (flaps, agonist, cleavage site)
     *
     * @param trajectory - Structure trajectory object
     */
    applyGainStyle(trajectory) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            // Step 1: Base cartoon backbone with solid color
            const structure = yield this.plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default', {
                representationPreset: 'polymer-cartoon',
                representationPresetParams: {
                    theme: {
                        globalName: 'uniform',
                    }
                }
            });
            // Get the structure root
            const rootStructure = this.plugin.managers.structure.hierarchy.current.structures[0];
            if (!rootStructure) {
                console.error('No structure loaded.');
                return;
            }
            const model = (_a = rootStructure.cell.obj) === null || _a === void 0 ? void 0 : _a.data;
            if (!model) {
                console.error('No model data available.');
                return;
            }
            // Step 2: Add white transparent surface over entire structure
            yield this.plugin.builders.structure.representation.addRepresentation(rootStructure.cell, {
                type: 'molecular-surface',
                typeParams: {
                    alpha: 0.9, // Transparency level
                    resolution: 0.6, // Surface resolution
                    probeRadius: 1.5, // Radius for solvent-accessible surface
                    smoothness: 2, // Surface smoothing factor
                    ignoreHydrogens: false // Hide hydrogens from surface calc
                },
                color: 'uniform',
                colorParams: { value: Color(0xE0E0E0) } // white surface
            });
            // Step 3: Highlight region A — flaps
            yield this.addColoredRegion(rootStructure, 650, 664, 0x8E44AD, 'gaussian-surface', 1); // Purple
            // Step 4: Highlight region B — agonist zone
            yield this.addColoredRegion(rootStructure, 838, 850, 0xFF5F1F, 'gaussian-surface', 1); // Orange
            // Step 5: Cleavage site — highlighted as blue transparent zone with atomic detail
            yield this.addColoredRegion(rootStructure, 837, 837, 0x0000FF, 'gaussian-surface', 0.3, true); // Blue with atoms
            // Step 6: Stylize lighting and outlines
            // await this.applyIllustrativeRendering();
        });
    }
    /**
     * Initializes and mounts the Mol* plugin to a container.
     * Also wires all UI interactions (buttons, toggles, inputs).
     *
     * @param target - DOM element or ID of container where viewer should render
     */
    init(target) {
        return __awaiter(this, void 0, void 0, function* () {
            // Create and configure the plugin
            this.plugin = yield createPluginUI({
                target: typeof target === 'string' ? document.getElementById(target) : target, // Resolve the target element
                render: renderReact18, // Use React 18 renderer
                spec: Object.assign(Object.assign({}, DefaultPluginUISpec()), { layout: {
                        initial: {
                            isExpanded: false, // Collapse sidebar on load
                            showControls: false, // Hide controls
                        }
                    }, config: [
                        // Disable UI controls for a minimal externalized experience
                        [PluginConfig.Viewport.ShowExpand, false],
                        [PluginConfig.Viewport.ShowControls, false],
                        [PluginConfig.Viewport.ShowSettings, false],
                        [PluginConfig.Viewport.ShowSelectionMode, false],
                        // [PluginConfig.Viewport.ShowAnimation, false]
                    ] })
            });
            // Automatically load a default structure with predefined style
            // await this.loadStructureWithStyle('1tqn', '/files/pdb/1crn.pdb'); // default PDB ID
            // Bind Mol* click event to local handler
            this.plugin.behaviors.interaction.click.subscribe(this.handleClick.bind(this));
            // Bind all UI elements to their respective event handlers
            this.bindUIEvents();
        });
    }
    /**
     * Handles click events inside the 3D viewer when selection mode is enabled.
     * Extracts residue info and triggers selection UI logic.
     *
     * @param event - Mol* click event (contains loci and context)
     */
    handleClick(event) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🖱️ Click detected - binding site mode:', this.bindingSiteSelectionMode, 'residue mode:', this.residueSelectionMode);
            // Handle multiple residue selection mode (priority over binding site mode)
            if (this.residueSelectionMode) {
                console.log('🧬 Processing multiple residue selection click...');
                yield this.handleResidueSelectionClick(event);
                return;
            }
            // Handle binding site selection mode
            if (this.bindingSiteSelectionMode) {
                console.log('🎯 Processing binding site click...');
                const handled = yield this.handleBindingSiteClick(event);
                if (handled) {
                    console.log('✅ Binding site click handled successfully');
                    return; // Click was successfully handled by binding site logic
                }
                console.log('❌ Binding site click not handled');
                // If not handled, continue with normal click processing (but this shouldn't happen)
            }
            // ? TODO : reactivate to use further selection functionnalities
            // if (!this.selectionMode) return; // Do nothing if selection mode is disabled
            // const selections = Array.from(
            //     this.plugin.managers.structure.selection.entries.values()
            // );
            // // Keep all the selected residues in a local array
            // const localSelected: any[] = [];
            // // Iterate over all selected structures
            // for (const { structure } of selections) {
            //     if (!structure) continue;
            //     // Loop over each residue in the selected structure
            //     Structure.eachAtomicHierarchyElement(structure, {
            //         residue: (loc) => {
            //             // const label_seq_id = StructureProperties.residue.label_seq_id(loc); // Extract sequence ID
            //             const label_seq_id = StructureProperties.residue.key
            //             if (label_seq_id) {
            //                 localSelected.push({ position: label_seq_id }); // Store result
            //             }
            //         },
            //     });
            // }
            // // Visually highlight and zoom to selection and display non-covalent interactions
            // this.applyFocusWithInteractions(localSelected);
            // // Update info panel to show residues selected
            // this.updatePanelFromSelection(localSelected.map(item => ({ displayName: `Residue ${item.position}` })));
        });
    }
    /**
     * Analyzes the current structure to identify all binding sites and create a consistent numbering.
     * Binding sites are identified by residues with name "STP".
     * They are sorted by chainGroupId and numbered starting from 1.
     * @returns true if analysis succeeded and mapping was created, false otherwise
     */
    analyzeBindingSites() {
        var _a, _b;
        console.log('🔬 Analyzing binding sites in current structure...');
        // Clear previous mapping
        this.bindingSiteMapping.clear();
        // Get the current structure
        const structure = (_b = (_a = this.plugin.managers.structure.hierarchy.current.structures[0]) === null || _a === void 0 ? void 0 : _a.cell.obj) === null || _b === void 0 ? void 0 : _b.data;
        if (!structure) {
            console.log('❌ No structure loaded - cannot analyze binding sites');
            return false;
        }
        // Collect per-STP-chain: chainGroupId → auth_asym_id (chain letter from PDB).
        // fpocket encodes pockets in consecutive chain letters (A=pocket1, B=pocket2, …).
        // Sorting by chain letter gives a platform-independent, file-order-independent mapping
        // that matches pocket_00001.pdb, pocket_00002.pdb, … on every environment.
        const stpChains = new Map(); // chainGroupId → auth_asym_id
        Structure.eachAtomicHierarchyElement(structure, {
            residue: (loc) => {
                const chainGroupId = loc.unit.chainGroupId;
                if (stpChains.has(chainGroupId))
                    return; // already recorded this chain
                let label_comp_id = null;
                let chainId = null;
                try {
                    const residueKey = StructureProperties.residue.key(loc);
                    const unit = loc.unit;
                    if (Unit.isAtomic(unit)) {
                        for (let i = 0; i < unit.elements.length; i++) {
                            const atomLoc = {
                                kind: 'element-location',
                                structure: loc.structure,
                                unit,
                                element: unit.elements[i]
                            };
                            if (StructureProperties.residue.key(atomLoc) === residueKey) {
                                label_comp_id = StructureProperties.atom.label_comp_id(atomLoc);
                                chainId = StructureProperties.chain.auth_asym_id(atomLoc);
                                break;
                            }
                        }
                    }
                }
                catch (e) { /* silently ignore */ }
                if (label_comp_id === 'STP' && chainId !== null) {
                    stpChains.set(chainGroupId, chainId);
                }
            },
        });
        // Sort by chain letter (A < B < C …) — this is the canonical fpocket pocket order.
        const bindingSites = Array.from(stpChains.entries())
            .sort(([, chainA], [, chainB]) => chainA.localeCompare(chainB))
            .map(([chainGroupId, chainId]) => ({ chainGroupId, chainId }));
        console.log(`🎯 Found ${bindingSites.length} binding sites (STP residues):`, bindingSites.map(b => `chain ${b.chainId} → pocket ${bindingSites.indexOf(b) + 1}`));
        // Map chainGroupId → pocket number (1-based)
        bindingSites.forEach(({ chainGroupId }, index) => {
            this.bindingSiteMapping.set(chainGroupId, index + 1);
        });
        console.log('✅ Binding site mapping (chainGroupId → pocket):', Array.from(this.bindingSiteMapping.entries()));
        return this.bindingSiteMapping.size > 0 || bindingSites.length === 0;
    }
    /**
     * Handles click events for binding site selection.
     * Extracts binding site information from clicked elements.
     *
     * @param event - Mol* click event (contains loci and context)
     * @returns true if the click was handled, false if it should be ignored
     */
    handleBindingSiteClick(event) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🔍 handleBindingSiteClick called');
            // TODO : clean event and that 
            // TEMPORARY: Debug for antoine ==> Capture event object for debugging
            this.captureEventObject(event);
            // Utilise la même approche que handleClick - récupère les sélections via Mol* selection manager
            const selections = Array.from(this.plugin.managers.structure.selection.entries.values());
            console.log('📊 Selections found:', selections.length);
            // Check if we have any selections
            if (selections.length === 0) {
                console.log('❌ No selections found');
                return false;
            }
            // Keep all the selected residues in a local array
            const localSelected = [];
            // Iterate over all selected structures
            for (const { structure } of selections) {
                if (!structure)
                    continue;
                // Loop over each residue in the selected structure
                Structure.eachAtomicHierarchyElement(structure, {
                    residue: (loc) => {
                        const label_seq_id = StructureProperties.residue.label_seq_id(loc);
                        let label_comp_id = null;
                        try {
                            // Get the residue key to identify which atoms belong to this residue
                            const residueKey = StructureProperties.residue.key(loc);
                            // Find the first atom that belongs to this specific residue
                            const unit = loc.unit;
                            if (Unit.isAtomic(unit)) {
                                for (let i = 0; i < unit.elements.length; i++) {
                                    const atomIndex = unit.elements[i];
                                    const atomLoc = {
                                        kind: 'element-location',
                                        structure: loc.structure,
                                        unit: unit,
                                        element: atomIndex
                                    };
                                    // Check if this atom belongs to the same residue
                                    const atomResidueKey = StructureProperties.residue.key(atomLoc);
                                    if (atomResidueKey === residueKey) {
                                        label_comp_id = StructureProperties.atom.label_comp_id(atomLoc);
                                        break; // Found the first atom of this residue, stop searching
                                    }
                                }
                            }
                        }
                        catch (e) {
                            // Silently handle error
                        }
                        const chainGroupId = loc.unit.chainGroupId;
                        if (label_seq_id) {
                            localSelected.push({
                                position: label_seq_id,
                                label_comp_id: label_comp_id,
                                chainGroupId: chainGroupId,
                                location: loc
                            });
                        }
                    },
                });
            }
            // Check if we have any selected residues
            if (localSelected.length === 0) {
                console.log('❌ No residues selected');
                return false;
            }
            console.log('🧬 Selected residues:', localSelected.length);
            // Analyze the first selected residue to determine if it's a binding site
            const firstResidue = localSelected[0];
            // Binding sites are identified by residue name "STP"
            const isBindingSite = firstResidue.label_comp_id === 'STP';
            let bindingSiteNumber = null;
            if (isBindingSite) {
                // The label_seq_id (residue sequence number) of STP residues in fpocket's
                // rec_withPockets.pdb directly encodes the pocket number: STP residue 1 = pocket 1,
                // STP residue 2 = pocket 2, etc. Using it directly is robust and environment-agnostic —
                // no dependency on Molstar-internal chainGroupId ordering which is non-deterministic.
                bindingSiteNumber = firstResidue.position; // position == label_seq_id
                if (!bindingSiteNumber) {
                    console.error(`❌ STP residue has no sequence number (label_seq_id). Cannot determine binding site.`);
                    return false;
                }
                // Successfully found binding site number
                console.log(`🎯 BINDING SITE DÉTECTÉ ! Residue: ${firstResidue.label_comp_id}:${firstResidue.position} → Binding Site ${bindingSiteNumber}`);
            }
            else {
                console.log(`🧬 RÉCEPTEUR DÉTECTÉ ! Residue: ${firstResidue.label_comp_id}:${firstResidue.position}, Position: ${firstResidue.position}`);
            }
            const bindingSiteInfo = {
                isBindingSite: isBindingSite,
                bindingSiteNumber: bindingSiteNumber,
                elementInfo: {
                    chainGroupId: firstResidue.chainGroupId,
                    position: firstResidue.position,
                    label_comp_id: firstResidue.label_comp_id,
                    location: firstResidue.location
                }
            };
            if (bindingSiteInfo.isBindingSite) {
                console.log('✅ Processing binding site selection...');
                // Update the binding site input field
                this.handleBindingSiteDetection(`Binding Site ${bindingSiteInfo.bindingSiteNumber}`, bindingSiteInfo.bindingSiteNumber);
                // Show temporary success message in toggle area
                this.updateBindingSiteMessage(`✅ Binding site ${bindingSiteInfo.bindingSiteNumber} selected!`);
                // For now, we select one binding site at a time, so clear previous selections AFTER processing
                setTimeout(() => {
                    this.plugin.managers.structure.selection.clear();
                }, 100);
                return true;
            }
            else {
                console.log('🧬 Receptor clicked - clearing binding site selection');
                // This is not a binding site (not STP) - clear any previously selected binding site
                this.clearBindingSiteSelection();
                // For now, we select one binding site at a time, so clear previous selections AFTER processing
                setTimeout(() => {
                    this.plugin.managers.structure.selection.clear();
                }, 100);
                return false;
            }
        });
    }
    /**
     * Handles click events for multiple residue selection.
     * Allows user to select multiple residues to create a custom binding site.
     *
     * @param event - Mol* click event (contains loci and context)
     */
    handleResidueSelectionClick(event) {
        return __awaiter(this, void 0, void 0, function* () {
            // Get selections from Mol* selection manager
            const selections = Array.from(this.plugin.managers.structure.selection.entries.values());
            if (selections.length === 0) {
                return;
            }
            // Collect selected residues
            const localSelected = [];
            for (const { structure } of selections) {
                if (!structure)
                    continue;
                Structure.eachAtomicHierarchyElement(structure, {
                    residue: (loc) => {
                        const label_seq_id = StructureProperties.residue.label_seq_id(loc);
                        let label_comp_id = null;
                        try {
                            // Get the residue key to identify which atoms belong to this residue
                            const residueKey = StructureProperties.residue.key(loc);
                            // Find the first atom that belongs to this specific residue
                            const unit = loc.unit;
                            if (Unit.isAtomic(unit)) {
                                for (let i = 0; i < unit.elements.length; i++) {
                                    const atomIndex = unit.elements[i];
                                    const atomLoc = {
                                        kind: 'element-location',
                                        structure: loc.structure,
                                        unit: unit,
                                        element: atomIndex
                                    };
                                    // Check if this atom belongs to the same residue
                                    const atomResidueKey = StructureProperties.residue.key(atomLoc);
                                    if (atomResidueKey === residueKey) {
                                        label_comp_id = StructureProperties.atom.label_comp_id(atomLoc);
                                        break; // Found the first atom of this residue, stop searching
                                    }
                                }
                            }
                        }
                        catch (e) {
                            // Silently handle error
                        }
                        const chainGroupId = loc.unit.chainGroupId;
                        if (label_seq_id) {
                            // Create copy of location object (Structure.eachAtomicHierarchyElement reuses loc object)
                            const residueKey = StructureProperties.residue.key(loc);
                            const unit = loc.unit;
                            let elementIndex = loc.element;
                            // Verify element belongs to correct residue, find correct one if needed
                            if (Unit.isAtomic(unit)) {
                                const testLoc = {
                                    kind: 'element-location',
                                    structure: loc.structure,
                                    unit: unit,
                                    element: loc.element
                                };
                                if (StructureProperties.residue.key(testLoc) !== residueKey) {
                                    // Find correct element index for this residue
                                    for (let i = 0; i < unit.elements.length; i++) {
                                        const testAtomLoc = {
                                            kind: 'element-location',
                                            structure: loc.structure,
                                            unit: unit,
                                            element: unit.elements[i]
                                        };
                                        if (StructureProperties.residue.key(testAtomLoc) === residueKey) {
                                            elementIndex = unit.elements[i];
                                            break;
                                        }
                                    }
                                }
                            }
                            // Store frozen copy of location
                            const locationCopy = {
                                kind: loc.kind,
                                structure: loc.structure,
                                unit: loc.unit,
                                element: elementIndex
                            };
                            localSelected.push({
                                position: label_seq_id,
                                label_comp_id: label_comp_id,
                                chainGroupId: chainGroupId,
                                location: locationCopy
                            });
                        }
                    },
                });
            }
            if (localSelected.length === 0) {
                return;
            }
            // Add new residues to selection (avoid duplicates)
            const addedResidues = [];
            for (const residue of localSelected) {
                const exists = this.selectedResidues.some(r => r.position === residue.position &&
                    r.chainGroupId === residue.chainGroupId);
                if (!exists) {
                    this.selectedResidues.push(residue);
                    addedResidues.push(`${residue.label_comp_id || '?'}:${residue.position || '?'}`);
                }
            }
            if (addedResidues.length > 0) {
                console.log(`✅ Selected: ${addedResidues.join(', ')} (${this.selectedResidues.length} total)`);
            }
            // Update UI with selected residues
            this.updateResidueSelectionUI();
            // Keep selection visible in Mol* (don't clear - allows multiple residue selection)
            // Mol* will maintain the green highlight for all selected residues
        });
    }
    /**
     * Calculates the centroid of selected residues using "centroid of centroids" method.
     * Step 1: Calculate center of mass for each residue (average of all its atoms).
     * Step 2: Calculate final centroid as average of all residue centers.
     */
    calculateCentroid(residues) {
        var _a, _b;
        if (residues.length === 0)
            return null;
        const structure = (_b = (_a = this.plugin.managers.structure.hierarchy.current.structures[0]) === null || _a === void 0 ? void 0 : _a.cell.obj) === null || _b === void 0 ? void 0 : _b.data;
        if (!structure)
            return null;
        const residuePoints = [];
        // Step 1: Calculate center of mass for each residue
        for (let i = 0; i < residues.length; i++) {
            const residue = residues[i];
            const loc = residue.location;
            if (!loc || !loc.unit || !Unit.isAtomic(loc.unit)) {
                continue;
            }
            const unit = loc.unit;
            const residueIndex = StructureProperties.residue.key(loc);
            const elementsArray = Array.from(unit.elements);
            // Sum all atom coordinates for this residue
            let residueX = 0;
            let residueY = 0;
            let residueZ = 0;
            let residueAtomCount = 0;
            for (const elementIndex of elementsArray) {
                const atomLoc = {
                    kind: 'element-location',
                    structure: loc.structure,
                    unit: unit,
                    element: elementIndex
                };
                // Only consider atoms belonging to this specific residue
                if (StructureProperties.residue.key(atomLoc) !== residueIndex)
                    continue;
                const x = StructureProperties.atom.x(atomLoc);
                const y = StructureProperties.atom.y(atomLoc);
                const z = StructureProperties.atom.z(atomLoc);
                if (x !== undefined && y !== undefined && z !== undefined) {
                    residueX += x;
                    residueY += y;
                    residueZ += z;
                    residueAtomCount++;
                }
            }
            // Calculate residue center of mass: average of all atom coordinates
            if (residueAtomCount > 0) {
                residuePoints.push({
                    x: residueX / residueAtomCount,
                    y: residueY / residueAtomCount,
                    z: residueZ / residueAtomCount
                });
            }
        }
        if (residuePoints.length === 0)
            return null;
        // Step 2: Calculate final centroid as average of all residue centers
        const totalX = residuePoints.reduce((sum, point) => sum + point.x, 0);
        const totalY = residuePoints.reduce((sum, point) => sum + point.y, 0);
        const totalZ = residuePoints.reduce((sum, point) => sum + point.z, 0);
        return {
            x: totalX / residuePoints.length,
            y: totalY / residuePoints.length,
            z: totalZ / residuePoints.length
        };
    }
    /**
     * Calculates and displays the centroid of selected residues
     */
    calculateAndDisplayCentroid() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.selectedResidues.length < 2) {
                return;
            }
            const centroid = this.calculateCentroid(this.selectedResidues);
            if (centroid) {
                yield this.displayCentroid(centroid);
            }
        });
    }
    /**
     * Displays the centroid as a fluorescent green sphere in Mol* viewer and zooms to it
     */
    displayCentroid(centroid) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            // Coerce to numbers so API string values don't break positioning or camera
            const x = Number(centroid.x);
            const y = Number(centroid.y);
            const z = Number(centroid.z);
            if (Number.isNaN(x) || Number.isNaN(y) || Number.isNaN(z)) {
                console.warn('[Mol*] Invalid centroid coordinates, skipping display');
                return;
            }
            // Issue a new token for this display operation
            const token = ++this.currentCentroidToken;
            // Remove previous centroid marker before creating new one
            yield this.removeCentroidMarker();
            // Check if a newer display operation has started (token mismatch)
            if (token !== this.currentCentroidToken) {
                console.log('[Mol*] Centroid display cancelled: newer operation started');
                return;
            }
            // Create PDB string with single atom at centroid position (columns 31-38 X, 39-46 Y, 47-54 Z)
            const xStr = x.toFixed(3).padStart(8);
            const yStr = y.toFixed(3).padStart(8);
            const zStr = z.toFixed(3).padStart(8);
            const pdbString = `HEADER    CENTROID MARKER
ATOM      1  C   STP A   1    ${xStr}${yStr}${zStr}  1.00 20.00           C
END
`;
            try {
                // Create data object and parse as trajectory
                const data = yield this.plugin.builders.data.rawData({
                    data: pdbString,
                    label: 'centroid_marker'
                });
                // Check again if cancelled
                if (token !== this.currentCentroidToken) {
                    console.log('[Mol*] Centroid display cancelled: newer operation started');
                    return;
                }
                const trajectory = yield this.plugin.builders.structure.parseTrajectory(data, 'pdb');
                // Check again if cancelled
                if (token !== this.currentCentroidToken) {
                    console.log('[Mol*] Centroid display cancelled: newer operation started');
                    return;
                }
                // Load structure into hierarchy
                yield this.plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default');
                // Check again if cancelled
                if (token !== this.currentCentroidToken) {
                    console.log('[Mol*] Centroid display cancelled: newer operation started');
                    return;
                }
                // Get the newly added structure (last in hierarchy)
                const structures = this.plugin.managers.structure.hierarchy.current.structures;
                const centroidStructure = structures[structures.length - 1];
                if (!centroidStructure) {
                    return;
                }
                // Add spacefill representation (neon fluorescent green centroid)
                const component = (_b = (_a = centroidStructure.components) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.cell;
                if (component) {
                    yield this.plugin.builders.structure.representation.addRepresentation(component, {
                        type: 'spacefill',
                        typeParams: { sizeFactor: 0.35 },
                        color: 'uniform',
                        // Bright neon green so it stands out vs predefined STP markers
                        colorParams: { value: Color(0x39FF14) }
                    });
                    // Check again if cancelled before zooming
                    if (token !== this.currentCentroidToken) {
                        console.log('[Mol*] Centroid display cancelled: newer operation started');
                        return;
                    }
                    // Zoom to the centroid marker (without transparency)
                    const structure = (_c = centroidStructure.cell.obj) === null || _c === void 0 ? void 0 : _c.data;
                    if (structure) {
                        // Create a loci for the centroid atom
                        const loci = Structure.Loci(structure);
                        // Focus camera on the centroid without transparency (only camera focus, no structure focus)
                        // Use larger radius values for less zoom
                        this.plugin.managers.camera.focusLoci(loci, {
                            minRadius: 20, // Minimum distance from object
                            extraRadius: 10, // Additional padding
                            durationMs: 500 // Smooth transition
                        });
                    }
                }
            }
            catch (error) {
                console.error('Error displaying centroid marker:', error);
            }
        });
    }
    /**
     * Updates the UI to show selected residues and centroid information
     */
    updateResidueSelectionUI() {
        const detailsText = document.getElementById('binding-site-details');
        const submitBtn = document.getElementById('submit-binding-site');
        if (!detailsText) {
            console.warn('[Mol*] binding-site-details element not found');
            return;
        }
        const calculateCentroidBtn = document.getElementById('calculate-centroid');
        if (this.selectedResidues.length === 0) {
            detailsText.textContent = 'No residues selected';
            if (submitBtn)
                submitBtn.classList.add('disabled');
            if (calculateCentroidBtn)
                calculateCentroidBtn.classList.add('disabled');
            // Clear stored data
            window.customBindingSiteData = null;
            return;
        }
        // Format residue list: "ResName1:Pos1, ResName2:Pos2, ..."
        const residueList = this.selectedResidues
            .map(r => `${r.label_comp_id || '?'}:${r.position}`)
            .join(', ');
        // Display residue list
        const displayText = `${this.selectedResidues.length} residue(s): ${residueList}`;
        detailsText.textContent = displayText;
        // Enable/disable calculate centroid button based on residue count
        if (calculateCentroidBtn) {
            if (this.selectedResidues.length >= 2) {
                calculateCentroidBtn.classList.remove('disabled');
            }
            else {
                calculateCentroidBtn.classList.add('disabled');
            }
        }
        // Validate: require at least 2 residues for submission
        if (this.selectedResidues.length < 2) {
            window.customBindingSiteData = null;
        }
        else {
            // Store residue data globally for backend processing
            window.customBindingSiteData = {
                type: 'custom',
                residues: this.selectedResidues.map(r => ({
                    position: r.position,
                    label_comp_id: r.label_comp_id,
                    chainGroupId: r.chainGroupId
                }))
            };
        }
        // Validate submit button using global function
        if (typeof window.validateBindingSiteSubmit === 'function') {
            window.validateBindingSiteSubmit();
        }
    }
    /**
     * Returns true if the structure ref is a centroid marker (single-atom green sphere).
     * Centroid markers have exactly one unit with one element.
     */
    isCentroidMarkerStructure(structureRef) {
        var _a, _b, _c, _d, _e;
        try {
            const structure = (_b = (_a = structureRef.cell) === null || _a === void 0 ? void 0 : _a.obj) === null || _b === void 0 ? void 0 : _b.data;
            if (!structure)
                return false;
            const units = (_c = structure.units) !== null && _c !== void 0 ? _c : structure.runits;
            if (!units)
                return false;
            let elementCount = 0;
            for (let i = 0; i < units.length; i++) {
                const u = units[i];
                elementCount += (_e = (_d = u === null || u === void 0 ? void 0 : u.elements) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0;
            }
            return elementCount > 0 && elementCount <= 2; // centroid = 1 atom
        }
        catch (_f) {
            return false;
        }
    }
    /**
     * Removes all centroid markers from the viewer.
     * Only removes structures that are centroid markers (single-atom), so the receptor is never removed even if it appears after the centroid in the hierarchy.
     */
    removeCentroidMarker() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const structures = this.plugin.managers.structure.hierarchy.current.structures;
                const structuresToRemove = structures.filter((s) => this.isCentroidMarkerStructure(s));
                if (structuresToRemove.length > 0) {
                    console.log(`[Mol*] Removing ${structuresToRemove.length} centroid marker(s)`);
                    for (const structure of structuresToRemove) {
                        yield this.plugin.managers.structure.hierarchy.remove([structure]);
                    }
                    yield new Promise(resolve => setTimeout(resolve, 100));
                }
            }
            catch (e) {
                console.warn('[Mol*] Error removing centroid markers:', e);
            }
        });
    }
    /**
     * Re-center the camera on the currently loaded main structure.
     * Used when switching binding-site modes so the user doesn't stay zoomed on the centroid marker.
     */
    recenterOnLoadedReceptor() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            try {
                const structures = this.plugin.managers.structure.hierarchy.current.structures;
                if (!structures || structures.length === 0)
                    return;
                // Prefer the first structure (usually the receptor) to avoid focusing on temporary markers.
                const structureRef = (_a = structures[0]) !== null && _a !== void 0 ? _a : structures[structures.length - 1];
                const structureData = (_c = (_b = structureRef === null || structureRef === void 0 ? void 0 : structureRef.cell) === null || _b === void 0 ? void 0 : _b.obj) === null || _c === void 0 ? void 0 : _c.data;
                if (!structureData)
                    return;
                const loci = Structure.Loci(structureData);
                this.plugin.managers.camera.focusLoci(loci, {
                    minRadius: 30,
                    extraRadius: 20,
                    durationMs: 450
                });
            }
            catch (e) {
                console.warn('[Mol*] recenterOnLoadedReceptor failed:', e);
            }
        });
    }
    /**
     * Clears the residue selection
     */
    clearResidueSelection() {
        return __awaiter(this, void 0, void 0, function* () {
            this.selectedResidues = [];
            this.updateResidueSelectionUI();
            // Clear all selections and highlights in Mol*
            this.plugin.managers.structure.selection.clear();
            this.plugin.managers.interactivity.lociSelects.deselectAll();
            // Remove centroid marker (the green sphere)
            yield this.removeCentroidMarker();
        });
    }
    /**
     * Handles binding site detection and updates the UI
     */
    handleBindingSiteDetection(residueName, bindingSiteNumber) {
        console.log('📝 Updating UI with binding site:', bindingSiteNumber);
        // Update the binding site input field (for binding site mode)
        const bindingSiteInput = document.getElementById('binding-site-input');
        const detailsText = document.getElementById('binding-site-details-bs');
        const submitBtn = document.getElementById('submit-binding-site');
        console.log('🔍 UI elements found:', {
            bindingSiteInput: !!bindingSiteInput,
            detailsText: !!detailsText,
            submitBtn: !!submitBtn
        });
        if (bindingSiteInput) {
            bindingSiteInput.value = bindingSiteNumber.toString();
            console.log('✅ Input field updated with value:', bindingSiteNumber);
        }
        if (detailsText) {
            detailsText.textContent = `Binding site ${bindingSiteNumber} selected`;
            console.log('✅ Details text updated');
        }
        // Validate submit button using global function
        if (typeof window.validateBindingSiteSubmit === 'function') {
            window.validateBindingSiteSubmit();
        }
    }
    /**
     * Updates the binding site message display next to the toggle
     */
    updateBindingSiteMessage(message, isTemporary = true) {
        const bindingSiteLabel = document.getElementById('toggle-sel-bindingsite-label');
        if (bindingSiteLabel) {
            // Store the original text if not already stored
            if (!bindingSiteLabel.dataset.originalText) {
                bindingSiteLabel.dataset.originalText = bindingSiteLabel.textContent || '';
            }
            // Update with the message
            bindingSiteLabel.textContent = message;
            // Only clear temporary messages after 3 seconds
            if (isTemporary) {
                setTimeout(() => {
                    if (bindingSiteLabel.dataset.originalText) {
                        bindingSiteLabel.textContent = bindingSiteLabel.dataset.originalText;
                    }
                }, 3000);
            }
        }
        // Fallback to binding-site-details-bs if toggle-sel-bindingsite-label is not present
        const detailsText = document.getElementById('binding-site-details-bs');
        if (detailsText && !bindingSiteLabel) {
            detailsText.textContent = message;
        }
    }
    /**
     * Clears the binding site selection from the input field
     */
    clearBindingSiteSelection() {
        // Reset the binding site label to default text
        const bindingSiteLabel = document.getElementById('toggle-sel-bindingsite-label');
        if (bindingSiteLabel) {
            bindingSiteLabel.dataset.originalText = 'Click a binding site to select it.';
            bindingSiteLabel.textContent = 'Click a binding site to select it.';
        }
        // Fallback to binding-site-details-bs if toggle-sel-bindingsite-label is not present
        const detailsText = document.getElementById('binding-site-details-bs');
        if (detailsText && !bindingSiteLabel) {
            detailsText.textContent = 'No binding site selected';
        }
    }
    /**
     * Updates the binding site input field when manually entered
     */
    updateBindingSiteInput(bindingSiteNumber) {
        console.log(`🔗 Updating binding site input to: ${bindingSiteNumber}`);
        // Update the input field if it exists
        const input = document.getElementById('binding-site-input');
        if (input) {
            input.value = bindingSiteNumber.toString();
        }
        // Update the details text (for binding site mode)
        const detailsText = document.getElementById('binding-site-details-bs');
        if (detailsText) {
            detailsText.textContent = `Binding site ${bindingSiteNumber} selected`;
        }
        // Update the submit button state
        const submitBtn = document.getElementById('submit-binding-site');
        if (submitBtn) {
            submitBtn.classList.remove('disabled');
        }
    }
    /**
     * Removes a specific structure from the viewer by PDB ID
     * For simplicity, we'll clear all structures and reload the remaining ones
     */
    removeStructure(pdbId) {
        try {
            // For now, we'll clear all structures when removing one
            // This is simpler and more reliable than trying to remove specific structures
            this.plugin.clear();
            // Clear binding site mapping when structure is removed
            this.bindingSiteMapping.clear();
            console.log(`[Mol*] Cleared all structures (removed: ${pdbId})`);
            return true;
        }
        catch (error) {
            console.error(`[Mol*] Error removing structure ${pdbId}:`, error);
            return false;
        }
    }
    /**
     * Clears all structures from the viewer
     */
    clearAllStructures() {
        try {
            this.plugin.clear();
            // Clear binding site mapping when all structures are cleared
            this.bindingSiteMapping.clear();
            console.log('[Mol*] Cleared all structures from viewer');
            return true;
        }
        catch (error) {
            console.error('[Mol*] Error clearing all structures:', error);
            return false;
        }
    }
    /**
     * Downloads the event object as a JSON file
     */
    downloadEventAsJSON(event) {
        try {
            const eventJSON = JSON.stringify(event, null, 2);
            const blob = new Blob([eventJSON], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'event-object.json';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            console.log('📁 Event object downloaded as event-object.json');
        }
        catch (e) {
            console.log('Could not download event object:', e);
        }
    }
}
export { MolStarWrapper }; // Export the class for external use
